import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from '../../../lib/cn';

/**
 * Tabs компонент для навигации
 */
const Tabs = ({
  children,
  defaultTab = 0,
  variant = 'default',
  size = 'md',
  className = '',
  ...props
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  const variants = {
    default: cn(
      'border-b border-border',
      'dark:border-gray-700',
      'text-primary font-medium'
    ),
    pills: cn(
      'bg-gray-100 text-gray-700 rounded-md',
      'dark:bg-gray-800 dark:text-gray-200',
      'font-medium'
    ),
    underline: cn(
      'border-b-2 border-transparent',
      'dark:border-transparent',
      'text-primary font-medium'
    ),
    unified: cn(
      'border-b-2 border-emerald-500',
      'text-emerald-600 dark:text-emerald-400',
      'font-black uppercase tracking-widest',
      'bg-emerald-50/50 dark:bg-emerald-900/10'
    ),
  };

  const sizes = {
    sm: 'text-xs px-3 py-2',
    md: 'text-sm px-4 py-2',
    lg: 'text-base px-6 py-3',
  };

  return (
    <div className={cn('w-full', className)} {...props}>
      {/* Tab Headers */}
      <div className={cn("flex space-x-1", variant === 'default' && "border-b border-border dark:border-gray-700")}>
        {React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) {
            return null;
          }

          // Use title prop to identify logical tabs
          const title = child.props.title || `Tab ${index + 1}`;
          const isActive = activeTab === index;
          const tabClasses = cn(
            'relative cursor-pointer transition-colors duration-200',
            'hover:bg-gray-50 dark:hover:bg-gray-700',
            'text-[var(--color-text-secondary)]', // Default inactive color
            isActive && variants[variant],
            sizes[size],
            !isActive && 'hover:text-[var(--color-text-primary)]'
          );

          return (
            <button
              key={index}
              className={tabClasses}
              onClick={() => handleTabClick(index)}
              role="tab"
              aria-selected={isActive}
            >
              {title}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) {
            return null;
          }

          if (activeTab === index) {
            return React.cloneElement(child, {
              className: cn('block', child.props.className),
            });
          }

          return React.cloneElement(child, {
            className: cn('hidden', child.props.className),
          });
        })}
      </div>
    </div>
  );
};

const Tab = ({ children, className = '', ...props }) => {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
};

Tabs.Tab = Tab;

Tabs.propTypes = {
  children: PropTypes.node.isRequired,
  defaultTab: PropTypes.number,
  variant: PropTypes.oneOf(['default', 'pills', 'underline', 'unified']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

Tab.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export { Tabs, Tab };
export default Tabs;